<template>
  <div class="modal-overlay" @click="$emit('close')">
    <div class="modal-container" @click.stop>
      <div class="modal-header">
        <h3>📦 Préparation Stock</h3>
        <button @click="$emit('close')" class="close-btn">×</button>
      </div>

      <div class="modal-body">
        <div class="intervention-info">
          <h4>{{ intervention?.prospect?.client?.nom }}</h4>
          <p>📅 {{ formatDate(intervention?.dateRdv) }}</p>
          <p>🔧 {{ intervention?.technicien?.prenom }} {{ intervention?.technicien?.nom }}</p>
        </div>

        <div class="products-section">
          <h4>Produits à préparer :</h4>
          <div v-if="!selectedProducts.length">
            <button @click="showSelector = true">+ Ajouter produits</button>
          </div>
          
          <div v-else>
            <div v-for="(product, index) in selectedProducts" :key="index" class="product-item">
              <span>{{ product.designation }}</span>
              <input type="number" v-model="product.quantite" min="1" max="5">
              <button @click="removeProduct(index)">×</button>
            </div>
            <button @click="showSelector = true">+ Ajouter</button>
          </div>
        </div>

        <div v-if="showSelector" class="product-selector">
          <input v-model="search" placeholder="Rechercher...">
          <div class="products-list">
            <div v-for="product in filteredProducts" :key="product._id" 
                 @click="addProduct(product)" class="product-option">
              {{ product.designation }} ({{ product.stockCount }} en stock)
            </div>
          </div>
          <button @click="showSelector = false">Fermer</button>
        </div>

        <textarea v-model="comments" placeholder="Commentaires..."></textarea>
      </div>

      <div class="modal-footer">
        <button @click="$emit('close')">Annuler</button>
        <button @click="prepareStock" :disabled="!selectedProducts.length || preparing">
          {{ preparing ? 'Préparation...' : 'Préparer' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useToast } from 'vue-toastification'

export default {
  name: 'StockPreparationModal',
  props: { intervention: Object },
  emits: ['close', 'stock-prepared'],
  setup(props, { emit }) {
    const toast = useToast()
    const preparing = ref(false)
    const showSelector = ref(false)
    const search = ref('')
    const comments = ref('')
    const selectedProducts = ref([])
    const availableProducts = ref([])

    const filteredProducts = computed(() => 
      availableProducts.value.filter(p => 
        p.designation.toLowerCase().includes(search.value.toLowerCase()) &&
        !selectedProducts.value.find(sp => sp._id === p._id)
      )
    )

    const loadProducts = async () => {
      try {
        const token = localStorage.getItem('token')
        const response = await fetch('/api/products?statut=en_stock', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (response.ok) {
          const data = await response.json()
          const groups = {}
          data.products.forEach(p => {
            if (!groups[p.referenceInterne]) {
              groups[p.referenceInterne] = { ...p, stockCount: 0, items: [] }
            }
            groups[p.referenceInterne].stockCount++
            groups[p.referenceInterne].items.push(p)
          })
          availableProducts.value = Object.values(groups)
        }
      } catch (error) {
        toast.error('Erreur chargement produits')
      }
    }

    const addProduct = (product) => {
      selectedProducts.value.push({
        ...product,
        quantite: 1
      })
      showSelector.value = false
    }

    const removeProduct = (index) => {
      selectedProducts.value.splice(index, 1)
    }

    const formatDate = (date) => new Date(date).toLocaleDateString('fr-FR')

    const prepareStock = async () => {
      preparing.value = true
      try {
        const token = localStorage.getItem('token')
        
        for (const product of selectedProducts.value) {
          const productsToMove = product.items.slice(0, product.quantite)
          
          for (const item of productsToMove) {
            // Créer mouvement
            await fetch('/api/movements', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                produit: item._id,
                type: 'sortie_technicien',
                quantite: 1,
                motif: `Intervention ${props.intervention.prospect?.client?.nom}`,
                rdvAssocie: props.intervention._id
              })
            })

            // Mettre à jour produit
            await fetch(`/api/products/${item._id}`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                statut: 'affecte_technicien'
              })
            })
          }
        }

        toast.success('✅ Stock préparé')
        emit('stock-prepared')
        emit('close')
      } catch (error) {
        toast.error('Erreur préparation')
      } finally {
        preparing.value = false
      }
    }

    onMounted(loadProducts)

    return {
      preparing, showSelector, search, comments, selectedProducts,
      filteredProducts, addProduct, removeProduct, formatDate, prepareStock
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal-container {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #eee;
}
.modal-body {
  padding: 1rem;
}
.product-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  margin-bottom: 0.5rem;
}
.product-option {
  padding: 0.5rem;
  cursor: pointer;
  border-bottom: 1px solid #eee;
}
.product-option:hover {
  background: #f5f5f5;
}
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid #eee;
}
button {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}
</style>
